import { FloatingOverlay } from '@floating-ui/react';
import { ReactNode, useCallback, useState } from 'react';
import Button from '@/components/elements/Button';
import Blockie from '@/components/elements/Blockie';
import Icon, { Icons } from '@/components/elements/Icon';
import { useTranslations, useFormatter } from 'use-intl';
import Link from 'next/link';
import { Address } from 'nftx.js';
import { useTransition, animated } from '@react-spring/web';
import useOnKeypress from '@/presentation/hooks/useOnKeypress';
import { Chain } from 'viem';

type Links = 'feedback' | 'docs' | 'discord' | 'twitter';
export type Page =
  | 'profile'
  | 'swap'
  | 'collections'
  | 'auctions'
  | 'info'
  | 'create';

const iconMap: { [key in Page]: Icons } = {
  profile: Icons.GLOBE,
  swap: Icons.ARROWS_RIGHT_LEFT,
  info: Icons.FUNNEL,
  collections: Icons.PHOTO,
  auctions: Icons.GAVEL,
  create: Icons.PLUS,
};

const footerLinks = [
  {
    href: 'https://ideas.nftx.io/nftx-v3',
    key: 'feedback' as Links,
    icon: Icons.TICKET,
    iconHover: '',
  },
  {
    href: 'https://docs.nftx.io/v/nftx-v3/',
    key: 'docs' as Links,
    icon: Icons.BOOK_OPEN_TEXT,
    iconHover: '',
  },
  {
    href: 'https://discord.gg/nftx-779073151115984926',
    key: 'discord' as Links,
    icon: Icons.DISCORD,
    iconHover: 'group-hover:text-[#7289DA]',
  },
  {
    href: 'https://x.com/nftx_',
    key: 'twitter' as Links,
    icon: Icons.X_LOGO,
    iconHover: 'group-hover:text-gray-800',
  },
];

const OverlayMenu = ({
  username,
  avatar,
  isConnected,
  balance,
  chainSlug,
  chain,
  activePage,
  address,
  connectButton,
  currencySelect,
  bountyButton,
  networkSelect,
  themeToggle,
}: {
  isConnected: boolean;
  username?: string | null | undefined;
  avatar?: string | null | undefined;
  balance?: number;
  chainSlug: string;
  activePage: Page;
  address?: Address;
  connectButton: ReactNode;
  bountyButton: ReactNode;
  currencySelect: ReactNode;
  networkSelect: ReactNode;
  themeToggle: ReactNode;
  chain: Chain;
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const t = useTranslations('Sidebar');
  const { number } = useFormatter();

  const transition = useTransition(showMenu, {
    from: {
      opacity: 0,
    },
    enter: {
      opacity: 1,
    },
    leave: {
      opacity: 0,
    },
  });

  const closeOnEsc = useCallback(() => setShowMenu(false), [setShowMenu]);
  useOnKeypress('Escape', closeOnEsc, showMenu);

  return (
    <>
      <Button
        variant="tertiary"
        size="medium"
        icon={Icons.LIST}
        onClick={() => setShowMenu(true)}
      />
      {transition(
        (style, show) =>
          show && (
            <animated.div style={style}>
              <FloatingOverlay
                className="bg-gray-50 z-50 flex flex-col text-gray-800"
                lockScroll={true}
              >
                <header className="flex h-[4.5rem] md:h-20 p-4 justify-end">
                  <Button
                    onClick={() => setShowMenu(false)}
                    icon={Icons.X_MARK}
                    variant="ghost"
                    className=""
                  />
                </header>
                <div className="px-6">{/* <NetworkSelect /> */}</div>
                <header className="px-8 md:py-2 flex">
                  {isConnected && (
                    <>
                      <section className="space-y-1">
                        <h3 className="font-bold text-sm">
                          <a
                            href={`${chain.blockExplorers?.default.url}/address/${address}`}
                            target="_blank"
                            rel="noreferrer nofollow"
                            className="hover:underline"
                          >
                            {username}
                          </a>
                        </h3>
                        <h4 className="text-2xl font-bold">
                          {'Ξ'}
                          {balance
                            ? number(balance, {
                                maximumFractionDigits: 4,
                                minimumFractionDigits: 0,
                              })
                            : '-'}
                        </h4>
                      </section>
                      <aside className="pl-4">
                        {address ? (
                          avatar ? (
                            <img
                              src={avatar}
                              className="w-12 h-12 rounded-sm"
                              alt={username || ''}
                            />
                          ) : (
                            <Blockie address={address} diameter={48} />
                          )
                        ) : null}
                      </aside>
                    </>
                  )}
                </header>
                <section className="px-8 py-4 flex-1 flex flex-col justify-center">
                  <nav>
                    <ul className="space-y-1 items-center">
                      {(
                        [
                          'profile',
                          'collections',
                          'swap',
                          'auctions',
                          'info',
                        ] as Page[]
                      ).map((page) => (
                        <li key={page}>
                          <Link
                            href={`/${chainSlug}/${page.toLowerCase()}`}
                            onClick={() => setShowMenu(false)}
                            className={`${
                              page === activePage
                                ? 'bg-gray-200/90'
                                : 'hover:bg-gray-100'
                            } py-3 px-4 text-gray-800 font-semibold flex items-center space-x-4 rounded-lg capitalize group transition-colors`}
                          >
                            <Icon
                              name={iconMap[page]}
                              size={20}
                              className={`transition-colors ${
                                page === activePage
                                  ? 'text-orange-500'
                                  : 'text-gray-400 group-hover:text-gray-500'
                              }`}
                            />
                            <span>{t(page)}</span>
                          </Link>
                        </li>
                      ))}
                      <li>
                        <Link
                          href={`/${chainSlug}/create`}
                          onClick={() => setShowMenu(false)}
                          className={`${
                            'create' === activePage
                              ? 'bg-gray-200/90'
                              : 'hover:bg-gray-100'
                          } py-3 px-4 text-gray-800 font-semibold flex items-center space-x-4 rounded-lg capitalize group transition-colors`}
                        >
                          <Icon
                            name={iconMap.create}
                            size={20}
                            className={`transition-colors ${
                              'create' === activePage
                                ? 'text-orange-500'
                                : 'text-gray-400 group-hover:text-gray-500'
                            }`}
                          />
                          <span>{t('create')}</span>
                        </Link>
                      </li>
                    </ul>
                  </nav>
                </section>
                <div className="mt-auto px-8 pb-6 md:py-4 md:pb-4">
                  <div className="mb-6 md:py-6 sm:flex justify-between items-center space-y-4 sm:space-y-0">
                    <ul className="space-x-2 flex text-sm font-bold uppercase">
                      {footerLinks.map((link) => (
                        <li key={link.href}>
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noreferrer nofollow"
                            className={`p-2 md:py-3 md:px-4 text-gray-800 font-semibold inline-flex md:flex items-center space-x-4 rounded-lg capitalize group transition-colors`}
                          >
                            <Icon
                              name={link.icon}
                              size={20}
                              className={`transition-colors text-gray-400 ${link.iconHover}`}
                            />
                            <span className="hidden md:block">
                              {t(link.key)}
                            </span>
                            <Icon
                              name={Icons.EXTERNAL}
                              className="hidden md:block opacity-0 group-hover:opacity-50 transition-opacity flex-none"
                              size={16}
                            />
                          </a>
                        </li>
                      ))}
                    </ul>
                    <div className="flex space-x-4">
                      {themeToggle}
                      {currencySelect}
                      {networkSelect}
                    </div>
                  </div>
                  {bountyButton}
                  {connectButton}
                </div>
                <footer className="text-xs text-gray-400 text-center mb-2">
                  {'v'}
                  {process.env.appVersion}
                </footer>
              </FloatingOverlay>
            </animated.div>
          )
      )}
    </>
  );
};

export default OverlayMenu;
